/*
 * Copyright (C) 2024 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and
 * should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly
 * prohibited.
 */

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
export interface ModalDialogProps {
  open: boolean;
  hideLeftBtn?: boolean;
  hideRightBtn?: boolean;
  leftBtnText?: string;
  rightBtnText?: string;
  title?: string | JSX.Element;
  message: string | JSX.Element;
  onLeftBtnClicked: () => void;
  onRightBtnClicked: () => void;
  onExited?: () => void;
  rightBtnColor?: string;
  rightBtnTextColor?: string;
}
const ModalDialog = ({
  open,
  hideLeftBtn = false,
  hideRightBtn = false,
  leftBtnText = 'No',
  rightBtnText = 'Yes',
  title = 'Confirm',
  message,
  onLeftBtnClicked,
  onRightBtnClicked,
  onExited,
  rightBtnColor = '#18ECFC',
  rightBtnTextColor = '#202E39',
}: ModalDialogProps) => {
  return (
    <Dialog
      open={open}
      TransitionProps={{ onExited }}
      PaperProps={{
        sx: {
          borderRadius: '12px',
        },
      }}
    >
      <DialogTitle
        sx={{
          fontStyle: 'normal',
          fontWeight: 700,
          fontSize: '20px',
          lineHeight: '28px',
          letterSpacing: 0.2,
          color: '#FFFFFF',
          maxHeight: '76px',
        }}
      >
        {title}
      </DialogTitle>
      <DialogContent>
        <DialogContentText
          sx={{
            fontStyle: 'normal',
            fontWeight: 400,
            fontSize: '16px',
            lineHeight: '24px',
            color: '#FFFFFF',
            flex: 'none',
            order: 0,
            whiteSpace: 'pre-line',
          }}
        >
          {message}
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ maxHeight: '88px', borderTop: '1px solid #32424E' }}>
        {!hideLeftBtn && (
          <Button
            variant="outlined"
            sx={{
              height: '40px',
              borderColor: '#ffffff',
              color: '#ffffff',
              fontFamily: 'Source Sans Pro',
              fontStyle: 'normal',
              fontWeight: 600,
              fontSize: '16px',
              lineHeight: '24px',
              marginRight: '8px',
              '&:hover': {
                borderColor: '#ffffff',
              },
            }}
            onClick={onLeftBtnClicked}
          >
            {leftBtnText}
          </Button>
        )}
        {!hideRightBtn && (
          <Button
            data-testid="modal-dialog-right-btn"
            variant="contained"
            sx={{
              outlineWidth: '0px',
              height: '40px',
              backgroundColor: rightBtnColor,
              color: rightBtnTextColor,
              fontFamily: 'Source Sans Pro',
              fontStyle: 'normal',
              fontWeight: 600,
              fontSize: '16px',
              lineHeight: '24px',
              marginRight: '8px',
              '&:hover': {
                backgroundColor: rightBtnColor,
                color: rightBtnTextColor,
              },
            }}
            onClick={onRightBtnClicked}
            autoFocus
          >
            {rightBtnText}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
export default ModalDialog;
